import { SWR_MODEL_VERSION, normalizeSWRParams } from './simulation/swrValidation';
import { normalizeSWRWithdrawalStrategy, normalizeSWRWithdrawalCustomization } from './simulation/swrWithdrawalStrategies';
import { SWR_CUSTOM_METHOD_VERSION } from './simulation/swrHistoricalPortfolio';
import { SWR_FLOW_METHOD_VERSION, SWR_DEBT_METHOD_VERSION, normalizeSWRTaxConfig, SWR_TAX_DATA_VERSION, SWR_TAX_DATA_HASH } from './simulation/swrTaxModel';
import { normalizeSWRDebtConfig } from './simulation/swrDebtModel';

const DB_NAME = 'longevity-income-architect';
const DB_VERSION = 1;
const STORE_NAME = 'swr-scenarios';
const STORAGE_KEY = 'lia:swr-scenarios';

export const SWR_SCENARIO_SCHEMA_VERSION = 4;
export const SWR_ENGINE_VERSION = SWR_MODEL_VERSION;
export const SWR_COMPARISON_MAX_SCENARIOS = 4;

const canUseIndexedDB = () => typeof window !== 'undefined' && 'indexedDB' in window;

const deepClone = (value) => {
  if (value === null || value === undefined) return value;
  return JSON.parse(JSON.stringify(value));
};

const normalizeScenario = (scenario) => {
  const sourceSchemaVersion = scenario?.schemaVersion || 1;
  return {
    ...scenario,
    schemaVersion: SWR_SCENARIO_SCHEMA_VERSION,
    ...(sourceSchemaVersion < SWR_SCENARIO_SCHEMA_VERSION ? { migratedFromSchemaVersion: sourceSchemaVersion } : {}),
    scenarioParams: {
      ...(scenario?.scenarioParams || {}),
      macroMode: scenario?.scenarioParams?.macroMode || 'fixed',
    },
  };
};

const createScenarioId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `swr-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const openDatabase = () => new Promise((resolve, reject) => {
  if (!canUseIndexedDB()) {
    reject(new Error('IndexedDB non disponibile'));
    return;
  }

  const request = window.indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      store.createIndex('savedAt', 'savedAt', { unique: false });
    }
  };
  request.onerror = () => reject(request.error || new Error('Errore apertura IndexedDB'));
  request.onsuccess = () => resolve(request.result);
});

const withStore = async (mode, callback) => {
  const db = await openDatabase();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, mode);
      const store = tx.objectStore(STORE_NAME);
      const result = callback(store);
      tx.oncomplete = () => resolve(result);
      tx.onerror = () => reject(tx.error || new Error('Errore IndexedDB'));
      tx.onabort = () => reject(tx.error || new Error('Transazione IndexedDB interrotta'));
    });
  } finally {
    db.close();
  }
};

const readFallbackScenarios = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeFallbackScenarios = (scenarios) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(scenarios));
};

export const listSWRScenarios = async () => {
  if (!canUseIndexedDB()) {
    return readFallbackScenarios().map(normalizeScenario).sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));
  }

  const scenarios = await withStore('readonly', (store) => {
    const request = store.getAll();
    return new Promise((resolve, reject) => {
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result || []);
    });
  });

  return scenarios.map(normalizeScenario).sort((a, b) => new Date(b.savedAt) - new Date(a.savedAt));
};

export const saveSWRScenario = async (scenario) => {
  if (!canUseIndexedDB()) {
    const scenarios = readFallbackScenarios().filter(item => item.id !== scenario.id);
    scenarios.unshift(scenario);
    writeFallbackScenarios(scenarios);
    return scenario;
  }

  await withStore('readwrite', (store) => store.put(scenario));
  return scenario;
};

const patchExistingSWRScenario = async (id, update) => {
  const merge = scenario => ({
    ...scenario,
    ...update(scenario),
    updatedAt: new Date().toISOString(),
  });

  if (!canUseIndexedDB()) {
    // No await between reading and writing: concurrent callbacks merge in one JS turn.
    const scenarios = readFallbackScenarios();
    const index = scenarios.findIndex(scenario => scenario.id === id);
    if (index < 0) return null;
    scenarios[index] = merge(scenarios[index]);
    writeFallbackScenarios(scenarios);
    return scenarios[index];
  }

  // IndexedDB serializes read/write transactions on this store. Read the latest
  // snapshot and patch only the requested fields in that same transaction.
  return withStore('readwrite', store => {
    const request = store.get(id);
    return new Promise((resolve, reject) => {
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        if (!request.result) { resolve(null); return; }
        const updated = merge(request.result);
        store.put(updated);
        resolve(updated);
      };
    });
  });
};

export const patchSWRScenarioResults = (id, resultPatch) => {
  const patch = deepClone(resultPatch);
  return patchExistingSWRScenario(id, scenario => ({ results: { ...(scenario.results || {}), ...patch } }));
};

export const renameSWRScenario = (id, name) => {
  const safeName = name?.trim();
  if (!safeName) throw new Error('Inserisci un nome per lo scenario.');
  return patchExistingSWRScenario(id, () => ({ name: safeName }));
};

export const deleteSWRScenario = async (id) => {
  if (!canUseIndexedDB()) {
    writeFallbackScenarios(readFallbackScenarios().filter(item => item.id !== id));
    return;
  }

  await withStore('readwrite', (store) => store.delete(id));
};

export const importSWRScenarios = async (scenarios) => {
  const normalizedScenarios = scenarios.map((scenario) => normalizeScenario({
    ...scenario,
    id: scenario.id || createScenarioId(),
    importedAt: new Date().toISOString(),
  }));

  if (!canUseIndexedDB()) {
    const existingById = new Map(readFallbackScenarios().map(item => [item.id, item]));
    normalizedScenarios.forEach(scenario => existingById.set(scenario.id, scenario));
    writeFallbackScenarios(Array.from(existingById.values()));
    return normalizedScenarios;
  }

  await withStore('readwrite', (store) => {
    normalizedScenarios.forEach(scenario => store.put(scenario));
  });
  return normalizedScenarios;
};

export const buildSWRScenarioSnapshot = ({ name, scenarioParams, results }) => {
  const savedAt = new Date().toISOString();
  const safeName = name?.trim() || `Scenario ${new Date(savedAt).toLocaleString('it-IT')}`;
  const finalCapitalPoint = Array.isArray(results?.capitalEvolution)
    ? results.capitalEvolution[results.capitalEvolution.length - 1]
    : null;

  return {
    id: createScenarioId(),
    schemaVersion: SWR_SCENARIO_SCHEMA_VERSION,
    appVersion: 'frontend-only',
    engineVersion: results?.engineModelVersion || 'unknown',
    name: safeName,
    savedAt,
    scenarioParams: deepClone({ ...scenarioParams,
      withdrawalStrategy: normalizeSWRWithdrawalStrategy(scenarioParams.withdrawalStrategy),
      withdrawalCustomization: normalizeSWRWithdrawalCustomization(scenarioParams.withdrawalCustomization),
      ...(results?.provenance ? { valuationDate: results.provenance.units.realMoneyBaseDate } : {}) }),
    results: {
      engineModelVersion: results?.engineModelVersion || 'unknown',
      provenance: deepClone(results?.provenance || null),
      summary: deepClone(results?.summary),
      withdrawalCustomization: deepClone(results?.withdrawalCustomization ?? null),
      contributionAnalysis: deepClone(results?.contributionAnalysis || null),
      drawdownStats: deepClone(results?.drawdownStats),
      capitalEvolution: deepClone(results?.capitalEvolution || []),
      depletionCumulativeByYear: deepClone(results?.depletionCumulativeByYear || []),
      perYearTableData: deepClone(results?.perYearTableData || []),
      returnParameterUncertainty: deepClone(results?.returnParameterUncertainty || null),
      historicalPortfolio: deepClone(results?.historicalPortfolio || null),
      taxation: deepClone(results?.taxation || null),
      ...(results?.debt ? { debt: deepClone(results.debt) } : {}),
      macroMode: results?.macroMode || scenarioParams?.macroMode || 'fixed',
      macroSeed: results?.macroSeed ?? null,
      macroAssumptions: deepClone(results?.macroAssumptions || null),
      macroSummary: deepClone(results?.macroSummary || null),
      macroEvolution: deepClone(results?.macroEvolution || []),
      safeWithdrawalAnalysis: deepClone(results?.safeWithdrawalAnalysis || null),
      stressTests: deepClone(results?.stressTests || null),
      finalCapitalPoint: deepClone(finalCapitalPoint),
    },
  };
};

export const normalizeImportedSWRFile = (payload) => {
  const scenarios = Array.isArray(payload) ? payload : Array.isArray(payload?.scenarios) ? payload.scenarios
    : payload?.scenarioParams && payload?.results ? [payload] : [];
  scenarios.forEach(scenario => {
    if (!scenario?.results || typeof scenario.results !== 'object') throw new Error('Lo scenario importato non contiene risultati validi.');
    // An unavailable historical edition remains readable; normalization for any
    // subsequent calculation still rejects the missing dataset or method.
    const input = scenario.scenarioParams;
    const factor = input?.preSimulationInflationFactor;
    if (factor != null && (typeof factor !== 'number' || !Number.isFinite(factor) || factor <= 0 || factor > 1e30)) {
      throw new Error('Fattore di indicizzazione importato non valido.');
    }
    const historical = input?.historicalPortfolio;
    if (historical != null && (typeof historical !== 'object' || typeof historical.enabled !== 'boolean')) {
      throw new Error('Configurazione storica importata non valida.');
    }
    if (historical?.enabled) {
      const rows = historical.allocations;
      const debt = historical.methodVersion === SWR_DEBT_METHOD_VERSION;
      const custom = historical.methodVersion === SWR_CUSTOM_METHOD_VERSION || debt;
      const total = Array.isArray(rows) ? rows.reduce((sum, r) => sum + (r?.weight || 0), 0) : 0;
      if (!Array.isArray(rows) || !rows.length || rows.length > 22
        || rows.some(r => typeof r?.assetId !== 'string' || !r.assetId || !Number.isFinite(r.weight) || r.weight < 0 || r.weight > (custom ? 2 : 1))
        || new Set(rows.map(r => r.assetId)).size !== rows.length
        || (custom ? total < 1 - 1e-8 || total > 2 + 1e-8 : Math.abs(total - 1) > 1e-8)
        || (custom && (!Number.isFinite(historical.annualFinancingSpread) || historical.annualFinancingSpread < 0 || historical.annualFinancingSpread > 1))
        || typeof historical.datasetVersion !== 'string' || typeof historical.datasetSha256 !== 'string') {
        throw new Error('Composizione o versione del portafoglio custom importato non valida.');
      }
      if (debt) normalizeSWRDebtConfig(historical.debt);
      if (historical.methodVersion === SWR_FLOW_METHOD_VERSION || debt) {
        // Structural validation uses a temporary known-edition copy only; retain
        // the original unavailable edition in the readable imported snapshot.
        normalizeSWRTaxConfig({ ...historical.taxation, datasetVersion: SWR_TAX_DATA_VERSION, datasetSha256: SWR_TAX_DATA_HASH }, rows, debt ? 1e15 * total : 1e15);
      }
    }
    // Structural reading does not reinterpret the saved economic convention.
    // New calculations validate consistency; the form derives its explicit draft.
    const structuralInput = { ...input, preSimulationInflationFactor: undefined };
    normalizeSWRParams(input?.historicalPortfolio?.enabled ? { ...structuralInput,
      historicalPortfolio: null, macroMode: input.cashReturnMode === 'macro-proxy' ? 'stochastic-euro-v1' : 'fixed', returnParameterUncertaintyEnabled: false,
      portfolioPreset: 'custom', modelPortfolio: null, simpleTaxProfile: null } : { ...structuralInput, simpleTaxProfile: null });
  });
  return scenarios;
};
